import React, { useEffect, useRef, useState } from "react";
import { X, Volume2, VolumeX } from "lucide-react";

const PROMO_VIDEO_URL =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_smart-buy-sell-20/artifacts/ce06sdao_clideo_editor_3b8da2acd0aa4717887dff89bdce133d%20%281%29.mp4";
// sessionStorage → resets when the browser/tab is closed, so the popup pops
// exactly once per fresh login/visit (not permanent like localStorage).
const STORAGE_KEY = "tradingox_promo_seen_v1";

export default function PromoVideoPopup() {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(false); // unmuted by default (user click provides gesture)
  const videoRef = useRef(null);

  // Trigger on the FIRST click/tap anywhere on the page — one shot per session.
  // A real click gives us the user gesture browsers require to autoplay with sound.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (err) {
      console.debug("[PromoVideo] sessionStorage read failed", err);
    }
    const trigger = () => {
      try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch (err) { console.debug("[PromoVideo] sessionStorage write failed", err); }
      setOpen(true);
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("click", trigger, { capture: true });
      window.removeEventListener("touchstart", trigger, { capture: true });
      window.removeEventListener("keydown", trigger);
    };
    // capture:true so we grab the click BEFORE it does anything else on the page
    window.addEventListener("click", trigger, { once: true, capture: true });
    window.addEventListener("touchstart", trigger, { once: true, capture: true, passive: true });
    window.addEventListener("keydown", trigger, { once: true });
    return cleanup;
  }, []);

  // As soon as the video element mounts, force play with sound (still inside the
  // user-gesture window, so most browsers allow it). If the browser blocks it,
  // fall back to muted playback so the video at least plays.
  useEffect(() => {
    if (!open || !videoRef.current) return;
    const v = videoRef.current;
    v.muted = false;
    v.volume = 1;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        // Browser refused unmuted autoplay → play muted, user can tap for sound
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    }
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (err) {
      console.debug("[PromoVideo] sessionStorage persist failed", err);
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (err) { console.debug("[PromoVideo] pause failed", err); }
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="promo-popup"
      className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        // Click on backdrop closes
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="relative w-full h-full sm:w-auto sm:h-[min(88vh,720px)] sm:aspect-[9/16] sm:rounded-2xl overflow-hidden sm:border sm:border-emerald-500/30 sm:shadow-[0_20px_80px_-10px_rgba(16,185,129,0.35)] bg-black">
        <video
          ref={videoRef}
          data-testid="promo-video"
          src={PROMO_VIDEO_URL}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          playsInline
          onEnded={close}
          onClick={(e) => {
            const v = e.currentTarget;
            if (v.paused) v.play().catch(() => {});
            else v.pause();
          }}
        />
        <button
          data-testid="promo-close"
          onClick={close}
          aria-label="Close promo video"
          className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full bg-black/70 hover:bg-black text-white border border-white/10 transition"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          data-testid="promo-mute"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className="absolute top-3 left-3 flex items-center gap-1.5 px-3 h-9 rounded-full bg-emerald-500/95 hover:bg-emerald-400 text-black text-xs font-bold transition"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          <span>{muted ? "Tap for sound" : "Sound on"}</span>
        </button>
      </div>
      <div className="hidden sm:block absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-slate-400 font-mono uppercase tracking-[0.25em]">
        press esc to dismiss
      </div>
    </div>
  );
}
